/**
 * Async establishment search for single-select pickers.
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { searchEstablishments } from '@/services/establishments.service';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import type { SelectOption } from '@/components/forms/Select';
import { formatEstablishmentSelectLabel } from '@/utils/format-establishment-select-label-utils';

/** Parse `"Jules (34)"` or `"Jules (Seine)"` → `{ nameQuery, department }`. */
function parseEstablishmentQuery(raw: string): { nameQuery: string; department?: string } {
  const trimmed = raw.trimEnd();
  if (!trimmed.endsWith(')')) return { nameQuery: raw };
  const open = trimmed.lastIndexOf('(');
  if (open <= 0) return { nameQuery: raw };
  const department = trimmed.slice(open + 1, -1).trim();
  if (!department) return { nameQuery: raw };
  return { nameQuery: trimmed.slice(0, open).trim(), department };
}

/** Typeahead over `GET /establishments/search` for single-select pickers. */
export const useEstablishmentsAutocomplete = (hookOptions?: {
  operationalOnly?: boolean;
  types?: string[];
}) => {
  const operationalOnly = hookOptions?.operationalOnly ?? false;
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const { nameQuery, department } = parseEstablishmentQuery(searchQuery);
  const types = hookOptions?.types;

  const { data, isLoading } = useQuery({
    queryKey: ['establishments-search', nameQuery, department, page, operationalOnly, types],
    queryFn: () => searchEstablishments(nameQuery, page, 5, department, { operationalOnly }, types),
    enabled: nameQuery.length >= 2 || (!!department && nameQuery.length >= 1),
  });

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (data?.hasMore) {
      setPage((p) => p + 1);
    }
  }, [data?.hasMore]);

  const options: SelectOption[] =
    data?.data?.map((establishment) => ({
      value: establishment.id,
      label: formatEstablishmentSelectLabel(establishment),
    })) || [];

  return {
    options,
    isLoading,
    hasMore: data?.hasMore || false,
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
  };
};

/** Create/edit pickers: validated prospection for in-person coordinator/manager only. Filters must keep using {@link useEstablishmentsAutocomplete}. */
export const useAssignmentEstablishmentsAutocomplete = (hookOptions?: { types?: string[] }) => {
  const { limitEstablishmentPickerToValidated } = useConnectedUser();
  return useEstablishmentsAutocomplete({
    ...hookOptions,
    operationalOnly: limitEstablishmentPickerToValidated,
  });
};
